// Color Swatch Visibility Handler - OPTIMIZED VERSION
// Only hides color swatches when the linked product is in draft/archived status
// Keeps all out-of-stock products visible so customers can discover them and sign up for notifications

(function() {
  'use strict';

  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  const CONCURRENT_REQUESTS = 8; // Max concurrent requests for better performance
  const REQUEST_TIMEOUT = 3000; // 3 second timeout per request
  const productStatusCache = new Map();

  /**
   * Check if a product URL is accessible (not draft/archived) with timeout
   * @param {string} url - Product URL to check
   * @returns {Promise<boolean>} - True if accessible, false if draft/archived
   */
  async function isProductAccessible(url) {
    // Check cache first
    const cached = productStatusCache.get(url);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.accessible;
    }

    try {
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout')), REQUEST_TIMEOUT)
      );

      // Use HEAD request to check if URL is accessible without downloading content
      const fetchPromise = fetch(url, { 
        method: 'HEAD',
        cache: 'no-store' // Don't cache the request itself
      });
      
      const response = await Promise.race([fetchPromise, timeoutPromise]);
      const accessible = response.ok; // 200-299 status codes
      
      // Cache the result
      productStatusCache.set(url, {
        accessible,
        timestamp: Date.now()
      });
      
      return accessible;
      
    } catch (error) {
      // Assume accessible on error (fail-safe) and cache for a shorter time
      productStatusCache.set(url, {
        accessible: true,
        timestamp: Date.now()
      });
      return true;
    }
  }

  /**
   * Process URLs in parallel batches for maximum performance
   * @param {Array} urlElementPairs - Array of {url, element, hideMultiple} objects
   */
  async function processUrlsInBatches(urlElementPairs) {
    if (urlElementPairs.length === 0) return;

    // Group by URL to avoid duplicate requests
    const urlGroups = new Map();
    urlElementPairs.forEach(pair => {
      if (!urlGroups.has(pair.url)) {
        urlGroups.set(pair.url, []);
      }
      urlGroups.get(pair.url).push(pair);
    });

    const uniqueUrls = Array.from(urlGroups.keys());
    
    // Process URLs in concurrent batches
    const batches = [];
    for (let i = 0; i < uniqueUrls.length; i += CONCURRENT_REQUESTS) {
      batches.push(uniqueUrls.slice(i, i + CONCURRENT_REQUESTS));
    }

    const allResults = new Map();
    
    // Process all batches in parallel
    for (const batch of batches) {
      const promises = batch.map(async (url) => {
        const accessible = await isProductAccessible(url);
        return { url, accessible };
      });
      
      const batchResults = await Promise.all(promises);
      batchResults.forEach(({ url, accessible }) => {
        allResults.set(url, accessible);
      });
    }

    // Apply results to elements - hide inaccessible products
    let hiddenCount = 0;
    
    allResults.forEach((accessible, url) => {
      if (!accessible) {
        const pairs = urlGroups.get(url);
        pairs.forEach(({ element, hideMultiple }) => {
          if (hideMultiple && Array.isArray(hideMultiple)) {
            hideMultiple.forEach(el => {
              if (el && el.style) el.style.display = 'none';
            });
          } else if (element && element.style) {
            element.style.display = 'none';
          }
          hiddenCount++;
        });
      }
    });

    if (hiddenCount > 0) {
      console.log(`[ColorSwatchVisibility] Hidden ${hiddenCount} inaccessible product swatches`);
    }
  }

  /**
   * Hide color swatches for inaccessible (draft/archived) products - OPTIMIZED
   */
  async function hideInaccessibleProductSwatches() {
    const urlElementPairs = [];

    // Collect all elements that need checking
    // Product detail page swatches
    const swatchesWithUrls = document.querySelectorAll('input[data-product-url] + label .color-swatch, input[data-product-url] + label .block-swatch, input[data-product-url] + label .thumbnail-swatch');
    
    for (const swatch of swatchesWithUrls) {
      const input = swatch.closest('label')?.previousElementSibling;
      const productUrl = input?.getAttribute('data-product-url');
      
      if (productUrl) {
        const label = swatch.closest('label');
        if (label && input) {
          urlElementPairs.push({
            url: productUrl,
            hideMultiple: [label, input] // Hide both label and input
          });
        }
      }
    }

    // Collection page swatches - updated selectors for unique IDs
    const collectionSwatchLinks = document.querySelectorAll(`
      [id^="colorComponent"] a[data-product-url], 
      .color-here a[data-product-url], 
      .this-is-here a[data-product-url],
      .color-swatch-link[data-product-url],
      a.color-swatch-link[data-product-url]
    `);
    
    console.log(`[ColorSwatchVisibility] Found ${collectionSwatchLinks.length} collection swatch links`);
    
    // Debug logging to understand the DOM structure
    if (collectionSwatchLinks.length === 0) {
      const debugContainers = document.querySelectorAll('[id^="colorComponent"], .color-here, .this-is-here');
      console.log(`[ColorSwatchVisibility] No swatch links found. Debugging ${debugContainers.length} containers:`);
      debugContainers.forEach((container, i) => {
        const allLinks = container.querySelectorAll('a');
        console.log(`[ColorSwatchVisibility] Container ${i} (${container.className}): ${allLinks.length} total links`);
        allLinks.forEach((link, j) => {
          console.log(`[ColorSwatchVisibility] Link ${j}: classes="${link.className}", data-product-url="${link.getAttribute('data-product-url')}", href="${link.href}"`);
        });
      });
    }
    
    for (const link of collectionSwatchLinks) {
      const productUrl = link.getAttribute('data-product-url');
      if (productUrl) {
        urlElementPairs.push({
          url: productUrl,
          element: link
        });
      }
    }

    // Regular swatch links with enhanced detection
    const swatchLinks = document.querySelectorAll('a.color-swatch, a.block-swatch, a.thumbnail-swatch, a.color-swatch-link');
    
    for (const link of swatchLinks) {
      let productUrl = link.getAttribute('data-product-url') || link.getAttribute('href');
      if (productUrl) {
        urlElementPairs.push({
          url: productUrl,
          element: link
        });
      }
    }

    // Additional fallback: Look for any links inside color containers (updated for unique IDs)
    const colorContainers = document.querySelectorAll('[id^="colorComponent"], .color-here, .this-is-here');
    for (const container of colorContainers) {
      const links = container.querySelectorAll('a');
      for (const link of links) {
        const productUrl = link.getAttribute('data-product-url') || link.getAttribute('href');
        if (productUrl && !urlElementPairs.some(pair => pair.element === link)) {
          console.log(`[ColorSwatchVisibility] Found fallback link: ${productUrl}`);
          urlElementPairs.push({
            url: productUrl,
            element: link
          });
        }
      }
    }

    // Process all collected URLs in optimized batches
    await processUrlsInBatches(urlElementPairs);
  }

  /**
   * Initialize the color swatch visibility handler
   */
  function init() {
    // Check immediately when page loads
    hideInaccessibleProductSwatches();

    // Enhanced mutation observer with more comprehensive detection
    let timeoutId;
    const observer = new MutationObserver((mutations) => {
      let foundNewContent = false;
      let detectionReasons = [];
      
      mutations.forEach(mutation => {
        Array.from(mutation.addedNodes).forEach(node => {
          if (node.nodeType === 1) { // Element node
            // Check for various swatch-related elements
            if (node.querySelector) {
              if (node.querySelector('.color-swatch, .block-swatch, .thumbnail-swatch, .color-swatch-link')) {
                foundNewContent = true;
                detectionReasons.push('Found color swatch elements');
              }
              if (node.querySelector('[id^="colorComponent"], .color-here, .this-is-here')) {
                foundNewContent = true;
                detectionReasons.push('Found color containers');
              }
              if (node.querySelector('.product-card, [class*="product"]')) {
                foundNewContent = true;
                detectionReasons.push('Found product cards');
              }
            }
            
            // Check the node itself (updated for unique IDs)
            if (node.classList) {
              if (node.classList.contains('color-swatch') || 
                  node.classList.contains('color-here') || 
                  node.classList.contains('this-is-here') ||
                  node.classList.contains('product-card') ||
                  node.classList.contains('color-swatch-link')) {
                foundNewContent = true;
                detectionReasons.push('Node itself is a relevant element');
              }
            }
            
            // Check for colorComponent ID pattern
            if (node.id && node.id.startsWith('colorComponent')) {
              foundNewContent = true;
              detectionReasons.push('Found colorComponent container');
            }
            
            // Check for any element with data-product-url
            if (node.getAttribute && node.getAttribute('data-product-url')) {
              foundNewContent = true;
              detectionReasons.push('Found element with data-product-url');
            }
          }
        });
      });

      if (foundNewContent) {
        clearTimeout(timeoutId);
        console.log(`[ColorSwatchVisibility] Detected new content: ${detectionReasons.join(', ')}`);
        timeoutId = setTimeout(() => {
          console.log('[ColorSwatchVisibility] Processing new content after mutation...');
          hideInaccessibleProductSwatches();
        }, 200); // Reduced delay for faster response
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Listen for Ajaxinate events (infinite scroll)
    document.addEventListener('DOMContentLoaded', () => {
      // Check for Ajaxinate completion events
      document.addEventListener('ajaxinate:loaded', (e) => {
        console.log('[ColorSwatchVisibility] Ajaxinate loaded new content, processing swatches immediately...');
        // Process immediately without delay for infinite scroll
        setTimeout(() => {
          hideInaccessibleProductSwatches();
          
          // Double-check: process again after a short delay in case DOM is still updating
          setTimeout(hideInaccessibleProductSwatches, 1000);
        }, 100);
      });

      // Listen for any custom events that might indicate new product loading
      document.addEventListener('products:loaded', (e) => {
        console.log('[ColorSwatchVisibility] Products loaded event detected, processing swatches immediately...');
        setTimeout(() => {
          hideInaccessibleProductSwatches();
          
          // Double-check: process again after a short delay
          setTimeout(hideInaccessibleProductSwatches, 1000);
        }, 100);
      });
    });

    // Also listen for scroll events to periodically check for new content
    let scrollTimeout;
    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const currentSwatchCount = document.querySelectorAll('.color-swatch, .color-here, .this-is-here, [id^="colorComponent"]').length;
        if (currentSwatchCount !== (window.lastSwatchCount || 0)) {
          console.log('[ColorSwatchVisibility] Swatch count changed during scroll, reprocessing...');
          window.lastSwatchCount = currentSwatchCount;
          hideInaccessibleProductSwatches();
        }
      }, 1000);
    });

    console.log('[ColorSwatchVisibility] Enhanced version initialized with Ajaxinate support');
  }

  // Expose functions for debugging
  window.ColorSwatchVisibility = {
    processSwatches: hideInaccessibleProductSwatches,
    checkProductAccessibility: isProductAccessible,
    clearCache: () => productStatusCache.clear()
  };

  // Additional debugging interface
  window.colorSwatchVisibility = {
    isProductAccessible,
    hideInaccessibleProductSwatches,
    clearCache: () => productStatusCache.clear(),
    getCacheSize: () => productStatusCache.size,
    processUrlsInBatches,
    showStats: () => {
      console.log(`Cache size: ${productStatusCache.size} URLs`);
      console.log('Cached URLs:', Array.from(productStatusCache.keys()));
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
